/* Standalone tweaks panel — floats bottom-right, persists to localStorage. */
import React from 'react';

const STORAGE_KEY = 'agenticorg.tweaks';

const PANEL_STYLE = `
.twk-panel{position:fixed;right:16px;bottom:16px;z-index:2147483646;width:280px;
  max-height:calc(100vh - 32px);display:flex;flex-direction:column;
  background:rgba(250,249,247,.92);color:#29261b;
  -webkit-backdrop-filter:blur(24px) saturate(160%);backdrop-filter:blur(24px) saturate(160%);
  border:.5px solid rgba(255,255,255,.6);border-radius:14px;
  box-shadow:0 1px 0 rgba(255,255,255,.5) inset,0 12px 40px rgba(0,0,0,.18);
  font:11.5px/1.4 ui-sans-serif,system-ui,-apple-system,sans-serif;overflow:hidden}
.twk-hd{display:flex;align-items:center;justify-content:space-between;
  padding:10px 8px 10px 14px;user-select:none}
.twk-hd b{font-size:12px;font-weight:600}
.twk-x{appearance:none;border:0;background:transparent;color:rgba(41,38,27,.55);
  width:22px;height:22px;border-radius:6px;cursor:pointer;font-size:13px;line-height:1}
.twk-x:hover{background:rgba(0,0,0,.06);color:#29261b}
.twk-body{padding:2px 14px 14px;display:flex;flex-direction:column;gap:10px;
  overflow-y:auto;min-height:0}
.twk-sect{font-size:10px;font-weight:600;letter-spacing:.06em;text-transform:uppercase;
  color:rgba(41,38,27,.45);padding:10px 0 4px}
.twk-sect:first-child{padding-top:0}
.twk-row{display:flex;flex-direction:column;gap:5px}
.twk-row-h{flex-direction:row;align-items:center;justify-content:space-between;gap:10px}
.twk-lbl{color:rgba(41,38,27,.72);font-weight:500}
.twk-seg{display:flex;padding:2px;border-radius:8px;background:rgba(0,0,0,.06)}
.twk-seg button{appearance:none;flex:1;border:0;background:transparent;color:inherit;
  font:inherit;font-weight:500;min-height:22px;border-radius:6px;cursor:pointer;
  padding:4px 6px;line-height:1.2}
.twk-seg button[data-on="1"]{background:rgba(255,255,255,.95);
  box-shadow:0 1px 2px rgba(0,0,0,.12)}
.twk-toggle{position:relative;width:32px;height:18px;border:0;border-radius:999px;
  background:rgba(0,0,0,.15);transition:background .15s;cursor:pointer;padding:0}
.twk-toggle[data-on="1"]{background:#34c759}
.twk-toggle i{position:absolute;top:2px;left:2px;width:14px;height:14px;border-radius:50%;
  background:#fff;box-shadow:0 1px 2px rgba(0,0,0,.25);transition:transform .15s}
.twk-toggle[data-on="1"] i{transform:translateX(14px)}
.twk-chips{display:flex;gap:6px}
.twk-chip{appearance:none;flex:1;height:28px;padding:0;border:0;border-radius:6px;
  cursor:pointer;box-shadow:0 0 0 .5px rgba(0,0,0,.12)}
.twk-chip[data-on="1"]{box-shadow:0 0 0 1.5px rgba(0,0,0,.85)}
.twk-btn{appearance:none;height:26px;padding:0 12px;border:0;border-radius:7px;
  background:rgba(0,0,0,.78);color:#fff;font:inherit;font-weight:500;cursor:pointer;
  text-align:center}
.twk-btn:hover{background:rgba(0,0,0,.88)}
.twk-btn.secondary{background:rgba(0,0,0,.06);color:inherit}
.twk-btn.secondary:hover{background:rgba(0,0,0,.1)}
.twk-fab{position:fixed;right:16px;bottom:16px;z-index:2147483646;width:42px;height:42px;
  border-radius:50%;border:0;background:#29261b;color:#fff;font-size:18px;cursor:pointer;
  box-shadow:0 6px 20px rgba(0,0,0,.25)}
`;

export function useTweaks(defaults) {
  const [values, setValues] = React.useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) return { ...defaults, ...JSON.parse(stored) };
    } catch {}
    return defaults;
  });

  const setTweak = React.useCallback((keyOrEdits, val) => {
    const edits = typeof keyOrEdits === 'object' && keyOrEdits !== null
      ? keyOrEdits : { [keyOrEdits]: val };
    setValues(prev => {
      const next = { ...prev, ...edits };
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch {}
      return next;
    });
  }, []);

  return [values, setTweak];
}

export function TweaksPanel({ title = 'Tweaks', children, open: openProp, onOpenChange }) {
  // Controlled mode when `open` is passed (App.jsx owns the toggle in
  // the topbar); uncontrolled mode otherwise (legacy floating FAB).
  const [internalOpen, setInternalOpen] = React.useState(false);
  const isControlled = openProp !== undefined;
  const open = isControlled ? openProp : internalOpen;
  const setOpen = v => {
    if (isControlled) onOpenChange?.(v);
    else setInternalOpen(v);
  };

  React.useEffect(() => {
    if (document.getElementById('twk-style')) return;
    const s = document.createElement('style');
    s.id = 'twk-style';
    s.textContent = PANEL_STYLE;
    document.head.appendChild(s);
  }, []);

  if (!open) {
    if (isControlled) return null;
    return (
      <button className="twk-fab" onClick={() => setOpen(true)} title="Tweaks">⚙</button>
    );
  }

  return (
    <div className="twk-panel" style={isControlled ? { top: 60, bottom: 'auto' } : undefined}>
      <div className="twk-hd">
        <b>{title}</b>
        <button className="twk-x" onClick={() => setOpen(false)}>×</button>
      </div>
      <div className="twk-body">{children}</div>
    </div>
  );
}

export function TweaksTrigger({ open, onToggle }) {
  return (
    <button
      type="button"
      className="btn-ghost"
      onClick={() => onToggle(!open)}
      title="Design tweaks"
      style={{padding:'6px 10px',fontSize:13,fontWeight:600}}
    >
      ⚙
    </button>
  );
}

export function TweakSection({ title, children }) {
  return (
    <>
      <div className="twk-sect">{title}</div>
      {children}
    </>
  );
}

export function TweakRadio({ label, value, options, onChange }) {
  return (
    <div className="twk-row">
      {label && <div className="twk-lbl">{label}</div>}
      <div className="twk-seg">
        {options.map(o => (
          <button
            key={o.value}
            data-on={value === o.value ? '1' : '0'}
            onClick={() => onChange(o.value)}
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}

export function TweakToggle({ label, value, onChange }) {
  return (
    <div className="twk-row twk-row-h">
      <span className="twk-lbl">{label}</span>
      <button className="twk-toggle" data-on={value ? '1' : '0'} onClick={() => onChange(!value)}>
        <i/>
      </button>
    </div>
  );
}

export function TweakColor({ label, value, options, onChange }) {
  return (
    <div className="twk-row">
      {label && <div className="twk-lbl">{label}</div>}
      <div className="twk-chips">
        {options.map(c => (
          <button
            key={c}
            className="twk-chip"
            data-on={value === c ? '1' : '0'}
            style={{background: c}}
            onClick={() => onChange(c)}
          />
        ))}
      </div>
    </div>
  );
}

export function TweakButton({ children, onClick }) {
  return (
    <button className="twk-btn secondary" onClick={onClick}>{children}</button>
  );
}

export function TweakRow({ children }) {
  return <div className="twk-row twk-row-h">{children}</div>;
}
